/**
 * Shown where a screen exists but this caller may not open it.
 *
 * It names the permission, on purpose. The alternative — "something went wrong"
 * — sends the user to a developer, who then has to work out which of thirty
 * permission names was missing. The set of permissions this build defines is not
 * secret; `GET /api/permissions` hands the whole list to any role editor.
 *
 * Note that this is not what *enforces* anything. It is what the router renders
 * instead of a screen that would be nothing but a 403.
 */
import { useTranslation } from 'react-i18next';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { EmptyState } from './EmptyState';

export interface NotPermittedProps {
  /** Holding any one of these would have been enough. */
  permissions: string[];
}

export function NotPermitted({ permissions }: NotPermittedProps) {
  const { t } = useTranslation('common');
  const many = permissions.length > 1;

  return (
    <Box sx={{ pt: 6 }}>
      <EmptyState
        icon={<LockOutlinedIcon fontSize="inherit" />}
        title={t('permission.cannotOpenTitle')}
        description={
          many ? t('permission.cannotOpenMany') : t('permission.cannotOpenOne')
        }
        action={
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            {permissions.map((permission) => (
              <Chip key={permission} label={permission} size="small" variant="outlined" />
            ))}
          </Stack>
        }
      />
    </Box>
  );
}
